import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { AdminTable } from "./AdminTable";
import { Plus, ArrowLeft, Save, Sparkles, ImagePlus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { RichTextEditor, RichTextPreview } from "../components/RichTextEditor";

const CATEGORIES = [
  "Vedic Astrology Basics",
  "Horoscope Analysis",
  "Mahadasha & Antardasha",
  "Astrology Remedies",
  "Career Astrology",
  "Marriage & Relationship Astrology",
  "Nakshatra Secrets",
  "Panchang & Muhurta",
  "Health & Medical Astrology",
];

const empty = {
  title: "", excerpt: "", content: "", category: "", cover_image: "",
  tags: "", published: false, featured: false, meta_title: "", meta_description: "",
  reading_time: 5, slug: "",
};

function slugify(t) {
  return (t || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

/** Estimate reading time (~200 wpm) from HTML content. */
function estimateReadTime(html) {
  const text = (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const words = text ? text.split(" ").length : 0;
  return Math.max(1, Math.round(words / 200));
}

function CountBadge({ current, max, testId }) {
  const overflow = current > max;
  return (
    <span data-testid={testId} className={`text-[11px] tabular-nums ${overflow ? "text-rose-500" : "text-foreground/50"}`}>
      {current}/{max}
    </span>
  );
}

function BlogEditor({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState(initial);
  const [autoSlug, setAutoSlug] = useState(!initial.id);
  const [tab, setTab] = useState("write"); // write | preview
  const [busy, setBusy] = useState(false);

  // Live slug + reading-time
  useEffect(() => {
    if (autoSlug) setForm((f) => ({ ...f, slug: slugify(f.title) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title, autoSlug]);
  useEffect(() => {
    setForm((f) => ({ ...f, reading_time: estimateReadTime(f.content) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.content]);

  const uploadCover = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file.");
    if (file.size > 5 * 1024 * 1024) return toast.error("Cover image must be under 5 MB.");
    const fd = new FormData();
    fd.append("file", file);
    const id = toast.loading("Uploading cover…");
    try {
      const { data } = await api.post("/admin/blog/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const url = data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
      setForm((f) => ({ ...f, cover_image: url }));
      toast.success("Cover image set", { id });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed", { id });
    }
  };

  const save = async (publish) => {
    if (!form.title.trim()) return toast.error("Please add a title.");
    if (!form.category) return toast.error("Please choose a category.");
    if (!form.excerpt.trim()) return toast.error("Please add a short excerpt.");
    if (!form.content || form.content === "<p></p>") return toast.error("Please add some content.");

    // Warn before silently unpublishing a Live post
    if (!publish && initial.id && initial.published) {
      const ok = window.confirm("This post is currently Live. Saving as draft will unpublish it. Continue?");
      if (!ok) return;
    }

    const payload = {
      ...form,
      tags: typeof form.tags === "string"
        ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
        : (form.tags || []),
      published: publish,
    };
    setBusy(true);
    try {
      if (initial.id) await api.put(`/admin/blog/${initial.id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success(publish ? (initial.published ? "Post updated" : "Post published") : "Draft saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="blog-editor" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onCancel} data-testid="editor-back" className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-1" />Back
          </Button>
          <div>
            <h1 className="font-serif text-3xl">{initial.id ? "Edit Post" : "New Post"}</h1>
            <p className="text-foreground/60 text-sm mt-0.5">
              {form.reading_time} min read · <span className={form.published ? "text-emerald-600" : "text-amber-600"}>{form.published ? "Live" : "Draft"}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-full border border-foreground/10 p-1 mr-1">
            <button type="button" onClick={() => setTab("write")} data-testid="tab-write"
              className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${tab === "write" ? "bg-[#D4AF37] text-[#0B1F3A]" : "text-foreground/60"}`}>Write</button>
            <button type="button" onClick={() => setTab("preview")} data-testid="tab-preview"
              className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${tab === "preview" ? "bg-[#D4AF37] text-[#0B1F3A]" : "text-foreground/60"}`}>Preview</button>
          </div>
          <Button variant="outline" onClick={() => save(false)} disabled={busy} data-testid="save-draft"
            className="rounded-full">
            <Save className="w-4 h-4 mr-2" />Save draft
          </Button>
          <Button onClick={() => save(true)} disabled={busy} data-testid="publish-post"
            className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold">
            <Sparkles className="w-4 h-4 mr-2" />{initial.published ? "Update" : "Publish"}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: Editor / Preview */}
        <div className="lg:col-span-2 space-y-4">
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Your post title…"
            className="text-3xl md:text-4xl font-serif border-0 border-b border-foreground/10 rounded-none focus-visible:ring-0 px-0 py-4 h-auto"
            data-testid="blog-title-input"
          />
          {tab === "write" ? (
            <RichTextEditor
              value={form.content}
              onChange={(html) => setForm((f) => ({ ...f, content: html }))}
              placeholder="Start writing… you can paste from Word, Google Docs, ChatGPT or Markdown and formatting will be preserved."
            />
          ) : (
            <div data-testid="preview-pane">
              {form.cover_image && (
                <img src={form.cover_image} alt="" className="w-full rounded-2xl mb-6 max-h-[380px] object-cover" />
              )}
              <RichTextPreview html={form.content} />
            </div>
          )}
        </div>

        {/* Right: Metadata */}
        <div className="space-y-5">
          {/* Cover image */}
          <div className="rounded-3xl border border-foreground/10 p-5 bg-background">
            <div className="eyebrow mb-3">Cover image</div>
            {form.cover_image ? (
              <div className="relative group">
                <img src={form.cover_image} alt="cover" className="w-full rounded-xl aspect-[16/9] object-cover" />
                <button type="button" onClick={() => setForm({ ...form, cover_image: "" })}
                  data-testid="cover-remove"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 rounded-full bg-black/70 text-white text-xs">Remove</button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-foreground/20 rounded-xl aspect-[16/9] cursor-pointer hover:border-[#D4AF37] transition-colors">
                <ImagePlus className="w-6 h-6 text-foreground/40 mb-1" />
                <div className="text-xs text-foreground/60">Click to upload</div>
                <div className="text-[10px] text-foreground/40 mt-1">JPG / PNG · Max 5 MB</div>
                <input type="file" accept="image/*" className="hidden"
                  data-testid="cover-upload"
                  onChange={(e) => uploadCover(e.target.files?.[0])} />
              </label>
            )}
            <Input
              value={form.cover_image || ""}
              onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
              placeholder="…or paste image URL"
              className="rounded-full mt-3 text-xs"
              data-testid="cover-url"
            />
          </div>

          {/* Category & tags */}
          <div className="rounded-3xl border border-foreground/10 p-5 bg-background space-y-4">
            <div className="eyebrow">Classification</div>
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="blog-category" className="rounded-full mt-2"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tags <span className="text-foreground/40">(comma-separated)</span></Label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="nakshatra, moon, remedies"
                data-testid="blog-tags" className="rounded-full mt-2" />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="featured-switch">Feature on homepage</Label>
              <Switch id="featured-switch" checked={form.featured}
                onCheckedChange={(v) => setForm({ ...form, featured: v })}
                data-testid="blog-featured" />
            </div>
          </div>

          {/* SEO */}
          <div className="rounded-3xl border border-foreground/10 p-5 bg-background space-y-4">
            <div className="flex items-center justify-between">
              <div className="eyebrow">SEO</div>
              <div className="text-[10px] uppercase tracking-widest text-foreground/40">Google preview</div>
            </div>
            {/* Google preview */}
            <div className="rounded-xl border border-foreground/10 p-3 bg-[#F4EFE6]/50 dark:bg-[#0F284B]/40">
              <div className="text-[13px] text-blue-700 dark:text-blue-400 truncate">{form.meta_title || form.title || "Untitled post"}</div>
              <div className="text-[11px] text-emerald-700 dark:text-emerald-400 truncate">yourastrohub.com/blog/{form.slug || "your-post"}</div>
              <p className="text-[12px] text-foreground/70 line-clamp-2 mt-1">
                {form.meta_description || form.excerpt || "This is what people will see in Google search results…"}
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Slug</Label>
                <button type="button" onClick={() => { setAutoSlug(true); setForm((f) => ({ ...f, slug: slugify(f.title) })); }}
                  data-testid="regen-slug"
                  className="text-[11px] text-[#D4AF37] hover:opacity-70 inline-flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" />Regenerate
                </button>
              </div>
              <Input value={form.slug || ""}
                onChange={(e) => { setAutoSlug(false); setForm({ ...form, slug: slugify(e.target.value) }); }}
                placeholder="url-friendly-slug"
                className="rounded-full mt-2 font-mono text-xs" data-testid="blog-slug" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Meta title</Label>
                <CountBadge current={(form.meta_title || form.title).length} max={60} testId="meta-title-count" />
              </div>
              <Input value={form.meta_title || ""}
                onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
                placeholder="Defaults to post title"
                className="rounded-full mt-2" data-testid="blog-meta-title" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Meta description</Label>
                <CountBadge current={(form.meta_description || form.excerpt).length} max={160} testId="meta-desc-count" />
              </div>
              <Textarea value={form.meta_description || ""}
                onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                placeholder="Defaults to excerpt if empty"
                rows={3} className="rounded-2xl mt-2" data-testid="blog-meta-description" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Excerpt *</Label>
                <CountBadge current={form.excerpt.length} max={200} testId="excerpt-count" />
              </div>
              <Textarea value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                placeholder="One-line teaser shown on the blog list"
                rows={3} className="rounded-2xl mt-2" data-testid="blog-excerpt" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// The above BlogEditor exposes the editor page inline.

export default function AdminBlog() {
  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState("list"); // list | edit
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/blog/all").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...empty }); setMode("edit"); };
  const openEdit = (r) => {
    setEditing({ ...r, tags: (r.tags || []).join(", ") });
    setMode("edit");
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    await api.delete(`/admin/blog/${r.id}`);
    toast.success("Deleted"); load();
  };

  if (mode === "edit" && editing) {
    return (
      <BlogEditor
        initial={editing}
        onCancel={() => setMode("list")}
        onSaved={() => { setMode("list"); load(); }}
      />
    );
  }

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-4xl">Blog Posts</h1>
          <p className="text-foreground/60 mt-1">Rich-text articles with SEO controls and live preview.</p>
        </div>
        <Button data-testid="new-blog-btn" onClick={openNew} className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold">
          <Plus className="w-4 h-4 mr-1" />New Post
        </Button>
      </div>

      <AdminTable
        testIdPrefix="blog-row"
        columns={[
          { key: "title", label: "Title" },
          { key: "category", label: "Category" },
          { key: "published", label: "Status", render: (r) => r.published
            ? <span className="text-emerald-600 text-xs font-semibold">● Live</span>
            : <span className="text-amber-600 text-xs font-semibold">● Draft</span> },
          { key: "created_at", label: "Created", render: (r) => new Date(r.created_at).toLocaleDateString() },
        ]}
        rows={rows}
        onEdit={openEdit}
        onDelete={del}
      />
    </div>
  );
}
